import { Badge, Banner, Button, Select } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useState, useEffect, useCallback } from 'react';
import { DataTable as Table } from '../components/ui/DataTable';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { ICONS } from '../components/ui/icons';
import { useAppToast } from '../hooks/useAppToast';
import { useCategories } from '../hooks/useCategories';
import { useTenant } from '../contexts/TenantContext';
import { SearchField } from '../components/ui/SearchField';
import { RowActions } from '../components/atlas/RowActions';
import { InlineEdit } from '../components/atlas/InlineEdit';
import { SortHeader, SortState, TablePager, sortBy } from '../components/atlas/DataTableParts';
import '../styles/atlas-lists.css';

type SegSortKey = 'name' | 'contactCount' | 'lastEvaluatedAt' | 'createdAt';

interface SegmentRule {
  // engagement: só em segmentos antigos (disparo em massa removido)
  field: 'criadoEm' | 'tag' | 'phone' | 'nome' | 'engagement' | 'email' | 'etiqueta' | 'consent' | 'whatsappStatus' | 'custom';
  op: string;
  value?: string | string[];
  key?: string; // campo personalizado
}

type StoredRules = SegmentRule[] | { match: 'all' | 'any'; rules: SegmentRule[] };
const readRules = (r: StoredRules): { match: 'all' | 'any'; rules: SegmentRule[] } =>
  (Array.isArray(r) ? { match: 'all', rules: r } : { match: r?.match === 'any' ? 'any' : 'all', rules: r?.rules ?? [] });

interface CustomFieldDef { id: string; key: string; name: string; type: string }

interface Segment {
  id: string;
  name: string;
  description?: string;
  rules: StoredRules;
  contactCount: number;
  lastEvaluatedAt: string | null;
  createdAt: string;
}

const FIELD_OPTIONS: { value: SegmentRule['field']; label: string }[] = [
  { value: 'criadoEm', label: 'Data de cadastro' },
  { value: 'tag', label: 'Categoria' },
  { value: 'nome', label: 'Nome do contato' },
  { value: 'phone', label: 'Telefone' },
  { value: 'email', label: 'E-mail' },
  { value: 'etiqueta', label: 'Etiqueta' },
  { value: 'consent', label: 'Consentimento (LGPD)' },
  { value: 'whatsappStatus', label: 'WhatsApp (status do número)' },
];

const OPS_BY_FIELD: Record<string, { value: string; label: string }[]> = {
  criadoEm: [
    { value: 'gte', label: 'A partir de' },
    { value: 'lte', label: 'Até' },
  ],
  tag: [{ value: 'in', label: 'É uma das categorias' }],
  nome: [{ value: 'contains', label: 'Contém' }, { value: 'notContains', label: 'Não contém' }, { value: 'startsWith', label: 'Começa com' }],
  email: [{ value: 'contains', label: 'Contém' }, { value: 'isNotEmpty', label: 'Está preenchido' }, { value: 'isEmpty', label: 'Está vazio' }],
  etiqueta: [{ value: 'in', label: 'Tem uma das etiquetas' }, { value: 'notIn', label: 'Não tem as etiquetas' }, { value: 'isEmpty', label: 'Sem etiqueta' }],
  consent: [{ value: 'eq', label: 'É' }],
  whatsappStatus: [{ value: 'eq', label: 'É' }],
  phone: [
    { value: 'contains', label: 'Contém' },
    { value: 'startsWith', label: 'Começa com' },
  ],
};

// Operadores dos campos personalizados, pelo tipo
const TEXT_OPS = [
  { value: 'eq', label: 'É igual a' }, { value: 'ne', label: 'É diferente de' },
  { value: 'contains', label: 'Contém' }, { value: 'notContains', label: 'Não contém' }, { value: 'startsWith', label: 'Começa com' },
  { value: 'isNotEmpty', label: 'Está preenchido' }, { value: 'isEmpty', label: 'Está vazio' },
];
const NUMBER_OPS = [
  { value: 'eq', label: 'É igual a' }, { value: 'ne', label: 'É diferente de' },
  { value: 'gt', label: 'Maior que' }, { value: 'gte', label: 'Maior ou igual a' }, { value: 'lt', label: 'Menor que' }, { value: 'lte', label: 'Menor ou igual a' },
  { value: 'isNotEmpty', label: 'Está preenchido' }, { value: 'isEmpty', label: 'Está vazio' },
];
const DATE_OPS = [
  { value: 'gte', label: 'A partir de' }, { value: 'lte', label: 'Até' },
  { value: 'isNotEmpty', label: 'Está preenchido' }, { value: 'isEmpty', label: 'Está vazio' },
];
const BOOL_OPS = [{ value: 'eq', label: 'É' }, { value: 'isEmpty', label: 'Está vazio' }];
function customOps(type?: string) {
  if (type === 'number') return NUMBER_OPS;
  if (type === 'date' || type === 'datetime') return DATE_OPS;
  if (type === 'boolean') return BOOL_OPS;
  return TEXT_OPS;
}
const NO_VALUE_OPS = ['isEmpty', 'isNotEmpty'];

const DATE_PRESETS = [
  { label: 'Últimos 7 dias', value: '-7d' },
  { label: 'Últimos 30 dias', value: '-30d' },
  { label: 'Últimos 90 dias', value: '-90d' },
  { label: 'Últimos 365 dias', value: '-365d' },
];

function emptyRule(): SegmentRule {
  return { field: 'criadoEm', op: 'gte', value: '-30d' };
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function SegmentsPage() {
  const toast = useAppToast();
  const { categories } = useCategories({ pageSize: 200 });

  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Segment | null>(null);
  const [deleting, setDeleting] = useState<Segment | null>(null);
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<SegSortKey>>({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formRules, setFormRules] = useState<SegmentRule[]>([emptyRule()]);
  const [formMatch, setFormMatch] = useState<'all' | 'any'>('all');
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);

  const authFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const loadSegments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch('/api/segments');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSegments(data.data || []);
    } catch {
      toast.error('Erro ao carregar segmentos');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  // Campos personalizados para as regras
  useEffect(() => {
    authFetch('/api/custom-fields')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCustomFields(d.fields || []))
      .catch(() => undefined);
  }, [authFetch]);

  function openNew() {
    setEditing(null);
    setFormName('');
    setFormDesc('');
    setFormRules([emptyRule()]);
    setFormMatch('all');
    setPreviewCount(null);
    setModalOpen(true);
  }

  function openEdit(seg: Segment) {
    setEditing(seg);
    setFormName(seg.name);
    setFormDesc(seg.description || '');
    const stored = readRules(seg.rules);
    setFormRules(stored.rules.length > 0 ? stored.rules : [emptyRule()]);
    setFormMatch(stored.match);
    setPreviewCount(seg.contactCount);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    setPreviewCount(null);
  }

  function addRule() {
    setFormRules((r) => [...r, emptyRule()]);
    setPreviewCount(null);
  }

  function removeRule(i: number) {
    setFormRules((r) => r.filter((_, idx) => idx !== i));
    setPreviewCount(null);
  }

  function updateRule(i: number, patch: Partial<SegmentRule>) {
    setFormRules((r) => {
      const copy = [...r];
      const updated = { ...copy[i], ...patch } as SegmentRule;
      if (patch.field || patch.key) {
        const ops = updated.field === 'custom' ? customOps(customFields.find((f) => f.key === updated.key)?.type) : OPS_BY_FIELD[updated.field] || [];
        updated.op = ops[0]?.value || 'gte';
        updated.value = updated.field === 'criadoEm' ? '-30d'
          : updated.field === 'tag' ? []
            : updated.field === 'consent' ? 'true'
                : updated.field === 'whatsappStatus' ? 'VALID'
                  : updated.field === 'custom' && customFields.find((f) => f.key === updated.key)?.type === 'boolean' ? 'true' : '';
        if (updated.field !== 'custom') delete updated.key;
      }
      copy[i] = updated;
      return copy;
    });
    setPreviewCount(null);
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const res = await authFetch('/api/segments/preview', {
        method: 'POST',
        body: JSON.stringify({ rules: { match: formMatch, rules: formRules } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      setPreviewCount(data.data.count);
    } catch (e: any) {
      toast.error(e.message || 'Erro ao calcular');
    } finally {
      setPreviewing(false);
    }
  }

  async function handleSave() {
    if (!formName.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      const body = { name: formName.trim(), description: formDesc || undefined, rules: { match: formMatch, rules: formRules } };
      const url = editing ? `/api/segments/${editing.id}` : '/api/segments';
      const method = editing ? 'PUT' : 'POST';
      const res = await authFetch(url, { method, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      toast.success(editing ? 'Segmento atualizado!' : 'Segmento criado!');
      closeModal();
      loadSegments();
    } catch (e: any) {
      toast.error(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }

  /** Renomear na própria linha (o PUT aceita só o nome; as regras ficam como estão) */
  async function renameSegment(seg: Segment, name: string) {
    if (!name) throw new Error('O nome é obrigatório');
    const res = await authFetch(`/api/segments/${seg.id}`, { method: 'PUT', body: JSON.stringify({ name }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao renomear');
    setSegments((list) => list.map((x) => (x.id === seg.id ? { ...x, name } : x)));
    toast.success('Segmento renomeado');
  }

  /** Duplicar: mesmo conjunto de regras, nome com "(cópia)" */
  async function duplicateSegment(seg: Segment) {
    try {
      const res = await authFetch('/api/segments', { method: 'POST', body: JSON.stringify({ name: `${seg.name} (cópia)`.slice(0, 100), description: seg.description || undefined, rules: seg.rules }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao duplicar');
      toast.success('Segmento duplicado');
      loadSegments();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao duplicar');
    }
  }

  async function copyId(seg: Segment) {
    try { await navigator.clipboard.writeText(seg.id); toast.success('ID do segmento copiado'); } catch { toast.error('Não foi possível copiar'); }
  }

  const q = search.trim().toLowerCase();
  const filtered = segments.filter((x) => !q || x.name.toLowerCase().includes(q) || (x.description || '').toLowerCase().includes(q));
  const sorted = sortBy(filtered, sort, (x, k) => (k === 'contactCount' ? x.contactCount : k === 'name' ? x.name : (x[k] || '')));
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pages);
  const visible = sorted.slice((current - 1) * pageSize, current * pageSize);
  const rulesText = (seg: Segment) => { const r = readRules(seg.rules); return `${r.rules.length} regra${r.rules.length !== 1 ? 's' : ''}${r.rules.length > 1 ? (r.match === 'any' ? ' (qualquer uma)' : ' (todas)') : ''}`; };

  async function handleDelete() {
    if (!deleting) return;
    try {
      const res = await authFetch(`/api/segments/${deleting.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erro ao excluir');
      toast.success('Segmento excluído');
      setDeleting(null);
      loadSegments();
    } catch {
      toast.error('Erro ao excluir segmento');
    }
  }

  if (modalOpen) {
    return (
      <FormPage
        backTo="/segmentos"
        backLabel="Segmentos"
        onBack={closeModal}
        title={editing ? 'Editar segmento' : 'Novo segmento'}
        description="Defina as regras para agrupar contatos automaticamente."
        submitLabel={editing ? 'Salvar alterações' : 'Criar segmento'}
        submitting={saving}
        onSubmit={handleSave}
        onCancel={closeModal}
      >
        <FormSection title="Detalhes">


        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: 'var(--text)' }}>
              Nome *
            </label>
            <input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ex: Leads dos últimos 30 dias"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 14,
                outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: 'var(--text)' }}>
              Descrição
            </label>
            <input
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Opcional"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 14,
                outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>Regras</label>
              <Button variant="secondary" icon={ICONS.Plus} onClick={addRule}>
                Adicionar Regra
              </Button>
            </div>

            {formRules.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: 'var(--text)' }}>
                <span>Contatos que atendem a</span>
                <Select aria-label="Combinar regras" value={formMatch} items={[{ value: 'all', label: 'todas as regras (E)' }, { value: 'any', label: 'qualquer uma das regras (OU)' }]} onValueChange={(v: string | null) => { setFormMatch((v ?? 'all') as 'all' | 'any'); setPreviewCount(null); }} />
              </div>
            )}

            {formRules.map((rule, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10,
                  backgroundColor: 'var(--surface-2)', borderRadius: 6, padding: '10px 12px',
                  border: '1px solid var(--border)',
                }}
              >
                {/* Field */}
                <div style={{ flex: 1.2 }}><Select aria-label="Campo" value={rule.field === 'custom' ? `custom:${rule.key}` : rule.field}
                    items={[...FIELD_OPTIONS.map((f) => ({ value: f.value, label: f.label })), ...customFields.map((f) => ({ value: `custom:${f.key}`, label: `${f.name} (campo personalizado)` }))]}
                    onValueChange={(v: string | null) => {
                      if (!v) return;
                      if (v.startsWith('custom:')) updateRule(i, { field: 'custom', key: v.slice(7) });
                      else updateRule(i, { field: v as SegmentRule['field'] });
                    }} /></div>

                {/* Operator */}
                <div style={{ flex: 1 }}><Select aria-label="Operador" value={rule.op}
                  items={(rule.field === 'custom' ? customOps(customFields.find((f) => f.key === rule.key)?.type) : OPS_BY_FIELD[rule.field] || []).map((o) => ({ value: o.value, label: o.label }))}
                  onValueChange={(v: string | null) => updateRule(i, { op: v ?? '' })} /></div>

                {/* Value */}
                {rule.field === 'criadoEm' && (
                  <div style={{ flex: 1.5 }}><Select aria-label="Período" value={typeof rule.value === 'string' ? rule.value : ''} items={DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }))} onValueChange={(v: string | null) => updateRule(i, { value: v ?? '' })} /></div>
                )}

                {rule.field === 'tag' && (
                  <select
                    multiple
                    value={Array.isArray(rule.value) ? rule.value : []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                      updateRule(i, { value: selected });
                    }}
                    style={{ flex: 1.5, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, fontFamily: 'inherit', minHeight: 64 }}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                )}

                {rule.field === 'engagement' && (
                  <span style={{ flex: 1.5, fontSize: 13, color: 'var(--crm-text-error)' }}>
                    Regra de campanha de disparo em massa (recurso removido): não seleciona ninguém. Troque ou remova esta regra.
                  </span>
                )}

                {(rule.field === 'nome' || rule.field === 'phone') && (
                  <input
                    value={typeof rule.value === 'string' ? rule.value : ''}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                    placeholder={rule.field === 'phone' ? 'Ex: 5511' : 'Ex: João'}
                    style={{ flex: 1.5, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, fontFamily: 'inherit' }}
                  />
                )}

                {(rule.field === 'email' || rule.field === 'etiqueta') && !NO_VALUE_OPS.includes(rule.op) && (
                  <input
                    value={typeof rule.value === 'string' ? rule.value : (rule.value || []).join(', ')}
                    onChange={(e) => updateRule(i, { value: rule.field === 'etiqueta' ? e.target.value.split(',').map((x) => x.trim()) : e.target.value })}
                    placeholder={rule.field === 'etiqueta' ? 'Ex: vip, cliente' : 'Ex: @empresa.com'}
                    style={{ flex: 1.5, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, fontFamily: 'inherit' }}
                  />
                )}

                {rule.field === 'consent' && (
                  <div style={{ flex: 1.5 }}><Select aria-label="Consentimento" value={String(rule.value ?? 'true')} items={[{ value: 'true', label: 'Deu consentimento' }, { value: 'false', label: 'Sem consentimento' }]} onValueChange={(v: string | null) => updateRule(i, { value: v ?? '' })} /></div>
                )}

                {rule.field === 'whatsappStatus' && (
                  <div style={{ flex: 1.5 }}><Select aria-label="WhatsApp" value={String(rule.value ?? 'VALID')} items={[{ value: 'VALID', label: 'Tem WhatsApp' }, { value: 'INVALID', label: 'Não tem WhatsApp' }, { value: 'UNKNOWN', label: 'Não verificado' }]} onValueChange={(v: string | null) => updateRule(i, { value: v ?? '' })} /></div>
                )}

                {rule.field === 'custom' && !NO_VALUE_OPS.includes(rule.op) && (() => {
                  const type = customFields.find((f) => f.key === rule.key)?.type;
                  if (type === 'boolean') {
                    return (
                      <div style={{ flex: 1.5 }}><Select aria-label="Valor" value={String(rule.value ?? 'true')} items={[{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]} onValueChange={(v: string | null) => updateRule(i, { value: v ?? '' })} /></div>
                    );
                  }
                  return (
                    <input
                      aria-label="Valor"
                      type={type === 'number' ? 'number' : type === 'date' ? 'date' : type === 'datetime' ? 'datetime-local' : 'text'}
                      value={typeof rule.value === 'string' ? rule.value : ''}
                      onChange={(e) => updateRule(i, { value: e.target.value })}
                      style={{ flex: 1.5, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: 13, fontFamily: 'inherit' }}
                    />
                  );
                })()}

                <button
                  type="button"
                  onClick={() => removeRule(i)}
                  disabled={formRules.length === 1}
                  style={{
                    background: 'none', border: 'none', cursor: formRules.length === 1 ? 'not-allowed' : 'pointer',
                    padding: 4, color: 'var(--crm-text-error)', opacity: formRules.length === 1 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center',
                  }}
                  title="Remover regra"
                  aria-label="Remover regra"
                >
                  <ICONS.Minus size={16} />
                </button>
              </div>
            ))}

            {/* Preview */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <Button variant="secondary" icon={ICONS.Refresh} onClick={handlePreview} loading={previewing}>
                Calcular contatos
              </Button>
              {previewCount !== null && (
                <span style={{ fontSize: 14, color: 'var(--brand-text)', fontWeight: 600 }}>
                  {previewCount} contato{previewCount !== 1 ? 's' : ''} correspondem
                </span>
              )}
            </div>
          </div>

          
        </div>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <div style={{ padding: '28px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Segmentos Dinâmicos</h2>
          <div style={{ color: 'var(--text-muted)' }}>
            Grupos de contatos definidos por regras automáticas, recalculados a cada uso.
          </div>
        </div>
        {canEdit && (
          <Button variant="primary" icon={ICONS.Plus} onClick={openNew}>
            Novo Segmento
          </Button>
        )}
      </div>

      {!loading && segments.length > 0 && (
        <div className="atlas-list-toolbar">
          <SearchField ariaLabel="Buscar segmentos" placeholder="Buscar segmento..." value={search} onChange={(value) => { setSearch(value); setPage(1); }} />
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
          {[1, 2, 3].map((k) => (
            <div key={k} style={{ height: 56, backgroundColor: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
      )}

      {!loading && segments.length === 0 && (
        <Banner variant="default" description={<>
          Nenhum segmento criado ainda. Clique em "Novo Segmento" para começar.
        </>} />
      )}

      {!loading && segments.length > 0 && (
        <div className="atlas-list-card">
          {filtered.length === 0 ? (
            <div className="atlas-list-empty"><div>Nenhum segmento encontrado.</div></div>
          ) : (
            <div className="overflow-x-auto">
              <Table
                variant="borderless"
                items={visible}
                columnDefinitions={[
                  {
                    id: 'segmento',
                    header: <SortHeader label="Segmento" sortKey="name" sort={sort} onSort={setSort} />,
                    cell: (seg: Segment) => (
                      <>
                        <InlineEdit
                          label="Nome do segmento"
                          value={seg.name}
                          disabled={!canEdit}
                          onSave={(name) => renameSegment(seg, name)}
                          render={(v) => <span className="atlas-list-name">{v}</span>}
                        />
                        {seg.description && <div className="atlas-list-sub">{seg.description}</div>}
                      </>
                    ),
                  },
                  {
                    id: 'contatos',
                    header: <SortHeader label="Contatos" sortKey="contactCount" sort={sort} onSort={setSort} />,
                    cell: (seg: Segment) => <Badge variant="green">{seg.contactCount.toLocaleString('pt-BR')}</Badge>,
                  },
                  {
                    id: 'regras',
                    header: 'Regras',
                    cell: (seg: Segment) => <div className="atlas-list-sub">{rulesText(seg)}</div>,
                  },
                  {
                    id: 'avaliado-em',
                    header: <SortHeader label="Avaliado em" sortKey="lastEvaluatedAt" sort={sort} onSort={setSort} />,
                    cell: (seg: Segment) => <div className="atlas-list-sub">{formatDate(seg.lastEvaluatedAt)}</div>,
                  },
                  {
                    id: 'criado-em',
                    header: <SortHeader label="Criado em" sortKey="createdAt" sort={sort} onSort={setSort} />,
                    cell: (seg: Segment) => <div className="atlas-list-sub">{new Date(seg.createdAt).toLocaleDateString('pt-BR')}</div>,
                  },
                  {
                    id: 'acoes',
                    header: 'Ações',
                    cell: (seg: Segment) => (
                      <RowActions
                        label={seg.name}
                        actions={[
                          ...(canEdit ? [
                            { label: 'Editar regras', onClick: () => openEdit(seg) },
                            { label: 'Duplicar', onClick: () => duplicateSegment(seg) },
                          ] : []),
                          { label: 'Copiar ID do segmento', onClick: () => copyId(seg) },
                        ]}
                        onDelete={canEdit ? () => setDeleting(seg) : undefined}
                      />
                    ),
                  },
                ]}
                ariaLabels={{ tableLabel: 'Segmentos', selectionGroupLabel: 'Segmentos' }}
              />
            </div>
          )}
          {filtered.length > 0 && <TablePager page={current} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />}
        </div>
      )}



      {/* Exclusão: digitar DELETE ou o nome do segmento */}
      <DangerConfirmModal
        open={!!deleting}
        title="Excluir segmento"
        confirmText="Excluir"
        objectName={deleting?.name}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        O segmento <strong>{deleting?.name}</strong> é excluído. Os contatos continuam cadastrados. Não dá para desfazer.
      </DangerConfirmModal>
    </div>
  );
}
